import React, { useState, useEffect } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import io from 'socket.io-client';
import { getSocketUrl } from '../utils/api';

interface OrderBookData {
  bids: Array<{ price: number; quantity: number }>;
  asks: Array<{ price: number; quantity: number }>;
}

const OrderBook: React.FC = () => {
  const [orderBook, setOrderBook] = useState<OrderBookData>({
    bids: [],
    asks: []
  });

  useEffect(() => {
    const serverUrl = getSocketUrl();
    
    console.log('🔗 호가창 서버 연결 시도:', serverUrl);
    
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: true
    });

    socket.on('orderBookUpdate', (data: OrderBookData) => {
      console.log('📊 오더북 데이터 수신:', data);
      setOrderBook(data);
    });

    socket.on('connect', () => {
      console.log('✅ 오더북 소켓 연결 성공');
    });

    socket.on('disconnect', () => {
      console.log('❌ 오더북 소켓 연결 끊김');
    });

    return () => {
      socket.off('orderBookUpdate');
      socket.off('connect');
      socket.off('disconnect');
      socket.disconnect();
    };
  }, []);

  const calculateTotal = (orders: Array<{ price: number; quantity: number }>) => {
    return orders.reduce((total, order) => total + (order.price * order.quantity), 0);
  };

  const maxQuantity = Math.max(
    ...orderBook.bids.map(b => b.quantity),
    ...orderBook.asks.map(a => a.quantity),
    1
  );

  return (
    <div className="bg-white dark:bg-dark-800 rounded-lg border border-light-300 dark:border-dark-700 flex flex-col" style={{ height: '650px', maxHeight: '650px' }}>
      {/* 헤더 */}
      <div className="flex items-center justify-between p-3 border-b border-light-300 dark:border-dark-700">
        <div className="flex items-center">
          <BookOpenIcon className="h-5 w-5 mr-2 text-blue-500" />
          <h3 className="font-medium text-gray-900 dark:text-white">오더북</h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-green-600 dark:text-green-400 font-medium">실시간</span>
        </div>
      </div>
      
      {/* 컬럼 헤더 */}
      <div className="flex justify-between px-4 py-1.5 bg-light-100 dark:bg-dark-700/50 border-b border-light-300 dark:border-dark-700">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-400 w-1/3 text-left">가격 (USDT)</span>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-400 w-1/3 text-center">수량 (BTC)</span>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-400 w-1/3 text-right">총액 (USDT)</span>
      </div>
      
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* 매도 주문 (Asks) - 상단 */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto scroll-smooth">
            <div className="space-y-0.5 p-1.5">
              {orderBook.asks.slice(0, 12).reverse().map((order, index) => (
                <div 
                  key={`ask-${index}`} 
                  className="relative flex justify-between items-center px-2 py-1 rounded hover:bg-red-500/5 transition-colors group"
                >
                  {/* 배경 바 */}
                  <div 
                    className="absolute inset-y-0 right-0 bg-red-500/8 rounded transition-all duration-300 group-hover:bg-red-500/12"
                    style={{ width: `${(order.quantity / maxQuantity) * 100}%` }}
                  />
                  
                  {/* 내용 */}
                  <span className="relative text-xs font-mono text-red-400 font-medium w-1/3 text-left">
                    ${order.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="relative text-xs font-mono text-gray-900 dark:text-white w-1/3 text-center">
                    {order.quantity.toFixed(5)}
                  </span>
                  <span className="relative text-xs font-mono text-gray-600 dark:text-gray-400 w-1/3 text-right">
                    ${(order.price * order.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* 현재가 구분선 */}
        <div className="py-2 px-4 bg-light-100 dark:bg-dark-700 border-y border-light-300 dark:border-dark-600 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">스프레드</span>
              <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 rounded border border-yellow-500/20 font-mono">
                ${orderBook.asks.length > 0 && orderBook.bids.length > 0 
                  ? (orderBook.asks[0].price - orderBook.bids[0].price).toFixed(2)
                  : '0.01'}
              </span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-500">
              {new Date().toLocaleTimeString('ko-KR', { hour12: false })}
            </span>
          </div>
        </div>
        
        {/* 매수 주문 (Bids) - 하단 */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto scroll-smooth">
            <div className="space-y-0.5 p-1.5">
              {orderBook.bids.slice(0, 12).map((order, index) => (
                <div 
                  key={`bid-${index}`} 
                  className="relative flex justify-between items-center px-2 py-1 rounded hover:bg-green-500/5 transition-colors group"
                >
                  {/* 배경 바 */}
                  <div 
                    className="absolute inset-y-0 right-0 bg-green-500/8 rounded transition-all duration-300 group-hover:bg-green-500/12"
                    style={{ width: `${(order.quantity / maxQuantity) * 100}%` }}
                  />
                  
                  {/* 내용 */}
                  <span className="relative text-xs font-mono text-green-600 dark:text-green-400 font-medium w-1/3 text-left">
                    ${order.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="relative text-xs font-mono text-gray-900 dark:text-white w-1/3 text-center">
                    {order.quantity.toFixed(5)}
                  </span>
                  <span className="relative text-xs font-mono text-gray-600 dark:text-gray-400 w-1/3 text-right">
                    ${(order.price * order.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* 하단 요약 정보 */}
        <div className="px-4 py-2 bg-light-100 dark:bg-dark-700/30 border-t border-light-300 dark:border-dark-700 flex-shrink-0">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">매수 총액</span>
              <span className="text-green-600 dark:text-green-400 font-medium font-mono">
                ${calculateTotal(orderBook.bids.slice(0, 12)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">매도 총액</span>
              <span className="text-red-600 dark:text-red-400 font-medium font-mono">
                ${calculateTotal(orderBook.asks.slice(0, 12)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderBook; 